/**
 * TestPrintFileDialog — "Test a Print File" debug downloader for the Print station.
 *
 * Enter a SKU (e.g. `2HT-XL-BK-SS37-R9`) → resolves the ripped print file via the
 * SAME backend resolver the real print path uses, and downloads it in the browser
 * WITHOUT printing and WITHOUT any DB write. Lets the print team confirm which
 * file resolves for a SKU without hunting for a real garment to scan.
 *
 * Backend: `trpc.printAgent.testPrintFile` (read-only stationProcedure). GCS /
 * Tencent return a 5-minute signed URL the browser saves directly; the signed
 * URL's last path segment is the filename, so the `.arx4` downloads with the
 * right name. Azure is agent-local and returns `azureUnsupported`.
 *
 * Scanner note: the SKU field is a REGULAR input (no `data-scanner-input`) so the
 * page hotkey engine skips keystrokes while typing — `[data-scanner-input]` fields
 * are explicitly allowed to pass hotkeys through (see useHotkeyEngine), which would
 * let a SKU like "…-P-…" fire the print hotkey. A USB scanner still types into the
 * focused field. Mirrors PrintIssueDialog's textarea pattern.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  FlaskConical,
  Download,
  Loader2,
  CheckCircle2,
  FileWarning,
  AlertTriangle,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { playSuccessBeep, playErrorBeep } from '@/lib/sounds';

type Side = 'front' | 'back' | undefined;
type Status = 'idle' | 'resolving' | 'success' | 'azure' | 'error';

interface TestPrintFileResult {
  downloadUrl: string | null;
  filename: string;
  isCustom: boolean;
  isDual: boolean;
  dualBackFilename: string | null;
  backend: 'azure' | 'tencent_cos' | 'gcs';
  expiresAt: string | null;
  azureUnsupported: boolean;
}

interface TestPrintFileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: string | null;
}

/** Minimum visible spinner so a fast resolve doesn't flash-flicker. */
const MIN_SPINNER_MS = 400;

const BACKEND_LABELS: Record<TestPrintFileResult['backend'], string> = {
  gcs: 'GCS',
  tencent_cos: 'Tencent COS',
  azure: 'Azure',
};

/** Save a file via a cross-origin signed URL (streams to disk, no Blob buffer). */
function triggerBrowserDownload(url: string, filename: string) {
  const a = document.createElement('a');
  a.href = url;
  // The download attribute is IGNORED cross-origin, so the actual save is forced
  // by the signed URL's `response-content-disposition: attachment; filename=…`
  // (minted backend-side in printAgent.testPrintFile). Without that the browser
  // renders the file inline (PNG opens in a tab) or hands it to an OS handler.
  // We keep the attribute as a same-origin-safe hint / belt-and-braces.
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function TestPrintFileDialog({ open, onOpenChange, locationId }: TestPrintFileDialogProps) {
  const { t } = useTranslation('production');
  const utils = trpc.useUtils();
  const inputRef = useRef<HTMLInputElement>(null);
  // Tracks the live `open` prop so an in-flight resolve that lands after the
  // operator dismissed the dialog can bail (no surprise download / stale state).
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const [sku, setSku] = useState('');
  const [barcode, setBarcode] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [result, setResult] = useState<TestPrintFileResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const reset = useCallback(() => {
    setSku('');
    setBarcode('');
    setStatus('idle');
    setResult(null);
    setErrorMsg(null);
  }, []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) reset();
      onOpenChange(next);
    },
    [onOpenChange, reset],
  );

  const isResolving = status === 'resolving';
  // Custom (CUST-) SKUs resolve from the SKU + the artwork barcode (the print
  // filename is built from both), so reveal + require the barcode for them.
  const isCustomSku = sku.trim().toUpperCase().startsWith('CUST-');
  const canSubmit =
    sku.trim().length > 0
    && !!locationId
    && !isResolving
    && (!isCustomSku || barcode.trim().length > 0);

  // Refocus the SKU field once a resolve settles, so the print team can test
  // SKUs back-to-back without reaching for the mouse.
  useEffect(() => {
    if (status === 'success' || status === 'azure' || status === 'error') {
      inputRef.current?.focus();
    }
  }, [status]);

  const resolve = useCallback(
    async (overrideSide?: Side) => {
      const trimmed = sku.trim();
      if (!trimmed || !locationId) return;
      // Barcode is only consulted for custom (CUST-) SKUs; ignored otherwise.
      const custom = trimmed.toUpperCase().startsWith('CUST-');
      const barcodeArg = custom && barcode.trim() ? barcode.trim() : undefined;
      setStatus('resolving');
      setErrorMsg(null);
      const startedAt = Date.now();
      const settleSpinner = async () => {
        const elapsed = Date.now() - startedAt;
        if (elapsed < MIN_SPINNER_MS) {
          await new Promise((r) => setTimeout(r, MIN_SPINNER_MS - elapsed));
        }
      };
      try {
        const res = await utils.printAgent.testPrintFile.fetch({
          sku: trimmed,
          locationId,
          ...(overrideSide ? { dualSide: overrideSide } : {}),
          ...(barcodeArg ? { artworkBarcode: barcodeArg } : {}),
        });
        await settleSpinner();
        // Operator dismissed the dialog mid-flight — don't fire a surprise
        // download or overwrite the reset state.
        if (!openRef.current) return;
        setResult(res);
        if (res.azureUnsupported || !res.downloadUrl) {
          setStatus('azure');
          return;
        }
        setStatus('success');
        playSuccessBeep();
        triggerBrowserDownload(res.downloadUrl, res.filename);
      } catch (err) {
        await settleSpinner();
        if (!openRef.current) return;
        setResult(null);
        setStatus('error');
        setErrorMsg(getUserMessage(err));
        playErrorBeep();
      }
    },
    [sku, barcode, locationId, utils],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2.5">
            <FlaskConical className="h-6 w-6 text-primary" aria-hidden="true" />
            {t('printer.testFile.title')}
          </DialogTitle>
          <DialogDescription>{t('printer.testFile.description')}</DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4 py-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) void resolve();
          }}
        >
          {/* SKU input */}
          <div className="space-y-2">
            <Label htmlFor="test-print-sku" className="text-sm font-medium">
              {t('printer.testFile.skuLabel')}
            </Label>
            <Input
              id="test-print-sku"
              ref={inputRef}
              value={sku}
              onChange={(e) => setSku(e.target.value.toUpperCase())}
              placeholder={t('printer.testFile.skuPlaceholder')}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              disabled={isResolving}
              className="h-14 text-lg font-mono tracking-wide"
            />
          </div>

          {/* Artwork barcode — revealed for custom (CUST-) SKUs, whose print file
              is keyed by the barcode. */}
          {isCustomSku && (
            <div className="space-y-2 animate-fade-in">
              <Label htmlFor="test-print-barcode" className="text-sm font-medium">
                {t('printer.testFile.barcodeLabel')}
              </Label>
              <Input
                id="test-print-barcode"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value.trim())}
                placeholder={t('printer.testFile.barcodePlaceholder')}
                autoComplete="off"
                spellCheck={false}
                disabled={isResolving}
                className="h-12 text-base font-mono"
              />
              <p className="text-xs text-muted-foreground">{t('printer.testFile.barcodeHint')}</p>
            </div>
          )}

          {/* Resolve & Download */}
          <Button
            type="submit"
            className="w-full h-14 text-lg font-semibold gap-2"
            disabled={!canSubmit}
          >
            {isResolving ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                {t('printer.testFile.resolving')}
              </>
            ) : (
              <>
                <Download className="h-5 w-5" aria-hidden="true" />
                {t('printer.testFile.download')}
              </>
            )}
          </Button>

          {/* Result area — fixed min-height so state swaps don't jump the layout */}
          <div className="min-h-[7rem]" aria-live="polite">
            {isResolving && (
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                <div className="h-5 w-2/3 rounded bg-muted animate-pulse" />
                <div className="flex gap-2">
                  <div className="h-5 w-20 rounded bg-muted animate-pulse" />
                  <div className="h-5 w-16 rounded bg-muted animate-pulse" />
                </div>
              </div>
            )}

            {status === 'success' && result && (
              <div className="rounded-lg border border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/30 p-4 animate-fade-in">
                <div className="flex items-start gap-3">
                  <CheckCircle2
                    className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5 animate-check-enter"
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1 space-y-2">
                    <p className="text-sm font-semibold text-green-900 dark:text-green-100">
                      {t('printer.testFile.downloaded')}
                    </p>
                    <p className="font-mono text-sm break-all text-foreground">{result.filename}</p>
                    <ResultBadges result={result} t={t} />
                    {result.isDual && (
                      <div className="pt-1">
                        <p className="text-xs text-muted-foreground mb-1.5">
                          {t('printer.testFile.dualHint')}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            disabled={isResolving}
                            onClick={() => void resolve('front')}
                          >
                            {t('printer.testFile.front')}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            disabled={isResolving}
                            onClick={() => void resolve('back')}
                          >
                            {t('printer.testFile.back')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {status === 'azure' && result && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-4 animate-fade-in">
                <div className="flex items-start gap-3">
                  <FileWarning
                    className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1 space-y-2">
                    <p className="font-mono text-sm break-all text-foreground">{result.filename}</p>
                    <ResultBadges result={result} t={t} />
                    <p className="text-sm text-amber-900 dark:text-amber-100">
                      {t('printer.testFile.azureUnsupported')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div
                role="alert"
                className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 flex items-start gap-3 animate-shake-error"
              >
                <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-destructive">
                    {t('printer.testFile.errorTitle')}
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5 break-words">{errorMsg}</p>
                </div>
              </div>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResultBadges({
  result,
  t,
}: {
  result: TestPrintFileResult;
  t: (key: string) => string;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <Badge variant="secondary" className="text-xs">
        {BACKEND_LABELS[result.backend]}
      </Badge>
      {result.isCustom && (
        <Badge variant="outline" className="text-xs">
          {t('printer.testFile.custom')}
        </Badge>
      )}
      {result.isDual && (
        <Badge variant="outline" className="text-xs">
          {t('printer.testFile.dual')}
        </Badge>
      )}
    </div>
  );
}
